import { createRoute } from "@tanstack/react-router";
import { OfficeOpenHoursScreen } from "@/app/Offices/Screens/OfficeOpenHoursScreen";
import { OfficeDetailsScreen } from "@/app/Offices/Screens/OfficeDetailsScreen";
import { OfficeRelocationsScreen } from "@/app/Offices/Screens/OfficeRelocationsScreen";
import { OfficeRecordScreen } from "@/app/Offices/Screens/OfficeRecordScreen";
import { OfficeListScreen } from "@/app/Offices/Screens/OfficeListScreen";
import { OfficeBookingsScreen } from "@/app/Offices/Screens/OfficeBookingsScreen";
import { OfficeCreateScreen } from "@/app/Offices/Screens/OfficeCreateScreen";
import { OfficeActivityScreen } from "@/app/Offices/Screens/OfficeActivityScreen";
import { OfficeEmptyScreen } from "@/app/Offices/Screens/OfficeEmptyScreen";
import { appLayoutRoute } from "@/routes/layout-routes";
import { z } from "zod";
import { getOfficeListQueryVariables } from "@/app/Offices/Utils/getOfficeListQueryVariables";
import {
  fetchQuery,
  prefetchInfiniteQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { supplierOfficeListQuery } from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { supplierOfficeRecordQuery } from "@/app/Offices/GraphQL/supplierOfficeRecordQuery";
import { supplierParamToId } from "@/app/Suppliers/Utils/globalSupplier";

const officeListSearchParams = z.object({
  officeSearch: z.string().optional(),
});

export type OfficeListSearchParams = z.infer<typeof officeListSearchParams>;

export const officeListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/offices",
  component: OfficeListScreen,
  validateSearch: officeListSearchParams,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, params }) => {
    const supplierId = supplierParamToId(params.supplierId);
    return prefetchInfiniteQuery(
      supplierOfficeListQuery,
      getOfficeListQueryVariables(deps, supplierId),
      params.orgId,
    );
  },
  head: () => ({
    meta: [
      {
        title: "Offices - Imoova Admin",
      },
      {
        name: "description",
        content: "Manage offices",
      },
    ],
  }),
});
export const officeCreateRoute = createRoute({
  getParentRoute: () => officeListRoute,
  path: "/create",
  component: OfficeCreateScreen,
  head: () => ({
    meta: [
      {
        title: "Create Office - Imoova Admin",
      },
    ],
  }),
});
export const officeEmptyRoute = createRoute({
  getParentRoute: () => officeListRoute,
  path: "/",
  component: OfficeEmptyScreen,
  head: () => ({
    meta: [
      {
        title: "Offices - Imoova Admin",
      },
    ],
  }),
});

export const officeRoute = createRoute({
  getParentRoute: () => officeListRoute,
  path: "$id",
  component: OfficeRecordScreen,
  loader: ({ params }) => {
    return fetchQuery(
      supplierOfficeRecordQuery,
      { id: params.id },
      params.orgId,
    );
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.supplierOffice.name ?? "Office"} - Imoova Admin`,
      },
    ],
  }),
});
export const officeDetailRoute = createRoute({
  getParentRoute: () => officeRoute,
  path: "/",
  component: OfficeDetailsScreen,
});
export const officeOpenHoursRoute = createRoute({
  getParentRoute: () => officeRoute,
  path: "/open-hours",
  component: OfficeOpenHoursScreen,
});
export const officeActivityRoute = createRoute({
  getParentRoute: () => officeRoute,
  path: "/activity",
  component: OfficeActivityScreen,
});
export const officeBookingsRoute = createRoute({
  getParentRoute: () => officeRoute,
  path: "/bookings",
  component: OfficeBookingsScreen,
});
export const officeRelocationsRoute = createRoute({
  getParentRoute: () => officeRoute,
  path: "/relocations",
  component: OfficeRelocationsScreen,
});
